/**
 * Evidence Cache Manager using IndexedDB
 *
 * Provides caching functionality for Evidence.dev report files with:
 * - File storage with metadata (content type, timestamp, size)
 * - Cache expiration policies by file type
 * - LRU eviction when approaching size limits
 * - Build version tracking for cache invalidation
 */

import { ref, type Ref } from 'vue'
import createLogger from '@/utils/logger'
import {
  isIndexedDbQuotaExceededError,
  isRecoverableEvidenceCacheError,
} from '@/utils/indexedDbTransientErrors'

const logger = createLogger('composables/useEvidenceCache')

// Database configuration
const DB_NAME = 'evidence-cache'
// v2: re-run schema migration for clients whose DB stayed at v1 without object stores
// (interrupted upgrade / partial state). Same-version opens skip onupgradeneeded, which
// caused NotFoundError on transaction(files).
const DB_VERSION = 2
const FILES_STORE = 'files'
const METADATA_STORE = 'metadata'

// Cache size limit (500MB)
const MAX_CACHE_SIZE_BYTES = 500 * 1024 * 1024

// Cache expiration times (in milliseconds)
const CACHE_EXPIRATION = {
  html: 24 * 60 * 60 * 1000, // 24 hours
  js: 24 * 60 * 60 * 1000, // 24 hours
  css: 24 * 60 * 60 * 1000, // 24 hours
  json: 1 * 60 * 60 * 1000, // 1 hour
  parquet: 7 * 24 * 60 * 60 * 1000, // 7 days
  arrow: 7 * 24 * 60 * 60 * 1000, // 7 days
  image: 30 * 24 * 60 * 60 * 1000, // 30 days
  default: 24 * 60 * 60 * 1000, // 24 hours
} as const

export interface CachedFile {
  path: string
  blob: Blob
  timestamp: number
  size: number
  contentType: string
  lastAccessed: number
}

export interface CacheStats {
  totalSize: number
  fileCount: number
  oldestFile: number | null
  newestFile: number | null
  buildHash: string | null
  lastSync: number | null
}

export interface CacheMetadata {
  key: string
  value: string | number
}

type FileType = keyof typeof CACHE_EXPIRATION

function isIndexedDbSupported(): boolean {
  return typeof indexedDB !== 'undefined'
}

/**
 * Get file type from path for cache expiration
 */
function getFileType(path: string): FileType {
  const ext = path.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'html':
      return 'html'
    case 'js':
    case 'mjs':
      return 'js'
    case 'css':
      return 'css'
    case 'json':
      return 'json'
    case 'parquet':
      return 'parquet'
    case 'arrow':
      return 'arrow'
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'webp':
    case 'svg':
    case 'ico':
    case 'woff':
    case 'woff2':
    case 'ttf':
      return 'image'
    default:
      return 'default'
  }
}

/**
 * Check if a cached file has expired
 */
function isExpired(file: CachedFile): boolean {
  const fileType = getFileType(file.path)
  const expirationTime = CACHE_EXPIRATION[fileType]
  return Date.now() - file.timestamp > expirationTime
}

/**
 * True when the DB has every store this composable uses.
 */
export function evidenceIndexedDbSchemaIsValid(db: IDBDatabase): boolean {
  return db.objectStoreNames.contains(FILES_STORE) && db.objectStoreNames.contains(METADATA_STORE)
}

function deleteEvidenceCacheDatabase(): Promise<void> {
  return new Promise((resolve, reject) => {
    const del = indexedDB.deleteDatabase(DB_NAME)
    del.onsuccess = () => resolve()
    del.onerror = () => reject(del.error ?? new Error('deleteDatabase failed'))
    del.onblocked = () => {
      logger.warn('evidence-cache deleteDatabase blocked; another tab may hold the database open')
      reject(new Error('deleteDatabase blocked by open connections'))
    }
  })
}

function attachVersionChangeHandler(db: IDBDatabase, onInvalidate: () => void): void {
  db.onversionchange = () => {
    logger.info('Evidence cache DB version changed in another tab; closing local connection')
    onInvalidate()
    db.close()
  }
}

/**
 * Open IndexedDB connection, validating schema and repairing once if needed.
 */
function openDatabaseAfterSchemaCheck(alreadyRetried: boolean): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      logger.error('Failed to open IndexedDB', request.error)
      reject(request.error)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      if (!db.objectStoreNames.contains(FILES_STORE)) {
        const filesStore = db.createObjectStore(FILES_STORE, { keyPath: 'path' })
        filesStore.createIndex('timestamp', 'timestamp', { unique: false })
        filesStore.createIndex('lastAccessed', 'lastAccessed', { unique: false })
        filesStore.createIndex('size', 'size', { unique: false })
      }

      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        db.createObjectStore(METADATA_STORE, { keyPath: 'key' })
      }
    }

    request.onsuccess = () => {
      const db = request.result

      if (!evidenceIndexedDbSchemaIsValid(db)) {
        if (alreadyRetried) {
          logger.error('Evidence cache DB is still missing object stores after delete and reopen')
          db.close()
          reject(new Error('Evidence cache database could not be repaired'))
          return
        }

        logger.warn(
          'Evidence cache IndexedDB opened without required object stores; deleting and recreating',
        )
        db.close()
        deleteEvidenceCacheDatabase()
          .then(() => openDatabaseAfterSchemaCheck(true))
          .then(resolve)
          .catch(reject)
        return
      }

      resolve(db)
    }
  })
}

function openDatabase(): Promise<IDBDatabase> {
  return openDatabaseAfterSchemaCheck(false)
}

/**
 * Evidence Cache Composable
 */
function useEvidenceCache() {
  const isInitialized = ref(false)
  const isAvailable = ref(false)
  const cacheSize: Ref<number> = ref(0)
  let db: IDBDatabase | null = null
  let initPromise: Promise<boolean> | null = null
  let initAttemptGeneration = 0

  function invalidateConnection(): void {
    initAttemptGeneration++
    if (db) {
      db.onversionchange = null
      db.close()
      db = null
    }
    isInitialized.value = false
    isAvailable.value = false
    initPromise = null
  }

  async function ensureDb(): Promise<IDBDatabase> {
    if (!isIndexedDbSupported()) {
      throw new Error('IndexedDB unavailable')
    }

    if (!db) {
      const ok = await init()
      if (!ok || !db) {
        throw new Error('Evidence cache unavailable')
      }
    }

    return db
  }

  async function withDbOperation<T>(
    operation: (database: IDBDatabase) => Promise<T>,
    retried = false,
  ): Promise<T> {
    try {
      const database = await ensureDb()
      return await operation(database)
    } catch (error) {
      if (!retried && isRecoverableEvidenceCacheError(error)) {
        logger.warn('Recoverable IndexedDB error in evidence cache; reconnecting', error)
        invalidateConnection()
        return withDbOperation(operation, true)
      }
      throw error
    }
  }

  /**
   * Initialize the cache database. Returns false when IDB is unavailable or open fails.
   */
  async function init(): Promise<boolean> {
    if (isInitialized.value && db) {
      return isAvailable.value
    }

    if (!isIndexedDbSupported()) {
      logger.warn('IndexedDB unavailable; evidence cache disabled')
      isAvailable.value = false
      return false
    }

    if (initPromise) {
      return initPromise
    }

    const generation = ++initAttemptGeneration
    initPromise = (async () => {
      try {
        const openedDb = await openDatabase()
        if (initAttemptGeneration !== generation) {
          openedDb.close()
          return false
        }
        db = openedDb
        attachVersionChangeHandler(db, invalidateConnection)
        isInitialized.value = true
        isAvailable.value = true
        await updateCacheSize()
        logger.info('Evidence cache initialized')
        return true
      } catch (error) {
        logger.error('Failed to initialize evidence cache', error)
        if (initAttemptGeneration === generation) {
          invalidateConnection()
        }
        return false
      } finally {
        if (initAttemptGeneration === generation) {
          initPromise = null
        }
      }
    })()

    return initPromise
  }

  /**
   * Get a file from cache
   */
  async function get(path: string): Promise<CachedFile | null> {
    return withDbOperation(
      (database) =>
        new Promise((resolve, reject) => {
          const transaction = database.transaction(FILES_STORE, 'readwrite')
          const store = transaction.objectStore(FILES_STORE)
          const request = store.get(path)

          let result: CachedFile | null = null
          let deletedSize = 0

          request.onerror = () => {
            logger.error('Failed to get file from cache', { path, error: request.error })
            reject(request.error)
          }

          request.onsuccess = () => {
            const file = request.result as CachedFile | undefined

            if (!file) {
              result = null
              return
            }

            if (isExpired(file)) {
              store.delete(path)
              deletedSize = file.size
              result = null
              return
            }

            file.lastAccessed = Date.now()
            store.put(file)
            result = file
          }

          transaction.oncomplete = () => {
            if (deletedSize > 0) {
              cacheSize.value = Math.max(0, cacheSize.value - deletedSize)
            }
            resolve(result)
          }

          transaction.onerror = () => {
            reject(transaction.error ?? new Error('Get transaction failed'))
          }
        }),
    )
  }

  async function putFile(
    path: string,
    blob: Blob,
    contentType: string,
    quotaRetry: boolean,
  ): Promise<void> {
    try {
      await withDbOperation(async (database) => {
        if (quotaRetry) {
          await evictForQuota(database)
        } else {
          await evictIfNeeded(blob.size, database)
        }

        const file: CachedFile = {
          path,
          blob,
          timestamp: Date.now(),
          size: blob.size,
          contentType,
          lastAccessed: Date.now(),
        }

        return new Promise<void>((resolve, reject) => {
          const transaction = database.transaction(FILES_STORE, 'readwrite')
          const store = transaction.objectStore(FILES_STORE)

          const getRequest = store.get(path)
          let oldSize = 0

          getRequest.onsuccess = () => {
            const existingFile = getRequest.result as CachedFile | undefined
            if (existingFile) {
              oldSize = existingFile.size
            }
            try {
              store.put(file)
            } catch (err) {
              reject(err)
            }
          }
          getRequest.onerror = () => {
            reject(getRequest.error ?? new Error('Cache read failed'))
          }

          transaction.oncomplete = () => {
            cacheSize.value = Math.max(0, cacheSize.value - oldSize + blob.size)
            resolve()
          }
          transaction.onerror = () => {
            reject(transaction.error ?? new Error('Cache write failed'))
          }
        })
      })
    } catch (error) {
      if (!quotaRetry && isIndexedDbQuotaExceededError(error)) {
        logger.warn('Evidence cache quota exceeded; evicting and retrying once', { path })
        return putFile(path, blob, contentType, true)
      }
      throw error
    }
  }

  /**
   * Store a file in cache
   */
  async function set(path: string, blob: Blob, contentType: string): Promise<void> {
    await putFile(path, blob, contentType, false)
  }

  /**
   * Delete a file from cache
   */
  async function remove(path: string): Promise<void> {
    await withDbOperation(
      (database) =>
        new Promise<void>((resolve, reject) => {
          const transaction = database.transaction(FILES_STORE, 'readwrite')
          const store = transaction.objectStore(FILES_STORE)

          const getRequest = store.get(path)
          let file: CachedFile | undefined

          getRequest.onerror = () => reject(getRequest.error)
          getRequest.onsuccess = () => {
            file = getRequest.result as CachedFile | undefined
            try {
              store.delete(path)
            } catch (err) {
              reject(err)
            }
          }

          transaction.oncomplete = () => {
            if (file) {
              cacheSize.value = Math.max(0, cacheSize.value - file.size)
            }
            resolve()
          }

          transaction.onerror = () => {
            reject(transaction.error ?? new Error('Remove transaction failed'))
          }
        }),
    )
  }

  /**
   * Clear all cached files
   */
  async function clear(): Promise<void> {
    if (!isInitialized.value || !db) {
      return
    }

    await withDbOperation(
      (database) =>
        new Promise<void>((resolve, reject) => {
          const transaction = database.transaction([FILES_STORE, METADATA_STORE], 'readwrite')
          const filesStore = transaction.objectStore(FILES_STORE)
          const metadataStore = transaction.objectStore(METADATA_STORE)

          filesStore.clear()
          metadataStore.clear()

          transaction.oncomplete = () => {
            cacheSize.value = 0
            logger.info('Evidence cache cleared')
            resolve()
          }

          transaction.onerror = () => {
            logger.error('Failed to clear cache', transaction.error)
            reject(transaction.error)
          }
        }),
    )
  }

  /**
   * Update cache size calculation
   */
  async function updateCacheSize(): Promise<number> {
    return withDbOperation(
      (database) =>
        new Promise<number>((resolve, reject) => {
          const transaction = database.transaction(FILES_STORE, 'readonly')
          const store = transaction.objectStore(FILES_STORE)
          const request = store.openCursor()

          let totalSize = 0

          request.onerror = () => reject(request.error)

          request.onsuccess = () => {
            const cursor = request.result
            if (cursor) {
              const file = cursor.value as CachedFile
              totalSize += file.size
              cursor.continue()
            } else {
              cacheSize.value = totalSize
              resolve(totalSize)
            }
          }
        }),
    )
  }

  /**
   * Get current cache size
   */
  async function getSize(): Promise<number> {
    await updateCacheSize()
    return cacheSize.value
  }

  /**
   * Evict oldest files if cache is approaching limit
   */
  async function evictIfNeeded(incomingSize: number, database?: IDBDatabase): Promise<void> {
    const activeDb = database ?? (await ensureDb())
    const currentSize = cacheSize.value
    const targetSize = MAX_CACHE_SIZE_BYTES - incomingSize

    if (currentSize <= targetSize) {
      return
    }

    const bytesToEvict = currentSize - targetSize

    return new Promise((resolve, reject) => {
      const transaction = activeDb.transaction(FILES_STORE, 'readwrite')
      const store = transaction.objectStore(FILES_STORE)
      const index = store.index('lastAccessed')
      const request = index.openCursor()

      let evictedBytes = 0
      const pathsToDelete: string[] = []

      request.onerror = () => reject(request.error)

      request.onsuccess = () => {
        const cursor = request.result
        if (cursor && evictedBytes < bytesToEvict) {
          const file = cursor.value as CachedFile
          pathsToDelete.push(file.path)
          evictedBytes += file.size
          cursor.continue()
        } else {
          pathsToDelete.forEach((path) => store.delete(path))

          transaction.oncomplete = () => {
            cacheSize.value = Math.max(0, cacheSize.value - evictedBytes)
            logger.info(`Evicted ${pathsToDelete.length} files (${evictedBytes} bytes)`)
            resolve()
          }
          transaction.onerror = () => {
            reject(transaction.error ?? new Error('Eviction transaction failed'))
          }
        }
      }
    })
  }

  async function evictForQuota(database: IDBDatabase): Promise<void> {
    await evictIfNeeded(MAX_CACHE_SIZE_BYTES / 2, database)
  }

  /**
   * Get cache statistics
   */
  async function getStats(): Promise<CacheStats> {
    return withDbOperation(async (database) => {
      const [totalSize, fileCount, timestamps, buildHash, lastSync] = await Promise.all([
        getSize(),
        getFileCount(database),
        getTimestampRange(database),
        getMetadata('buildHash'),
        getMetadata('lastSync'),
      ])

      return {
        totalSize,
        fileCount,
        oldestFile: timestamps.oldest,
        newestFile: timestamps.newest,
        buildHash: buildHash as string | null,
        lastSync: lastSync as number | null,
      }
    })
  }

  function getFileCount(database: IDBDatabase): Promise<number> {
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(FILES_STORE, 'readonly')
      const store = transaction.objectStore(FILES_STORE)
      const request = store.count()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
    })
  }

  function getTimestampRange(
    database: IDBDatabase,
  ): Promise<{ oldest: number | null; newest: number | null }> {
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(FILES_STORE, 'readonly')
      const store = transaction.objectStore(FILES_STORE)
      const index = store.index('timestamp')

      let oldest: number | null = null
      let newest: number | null = null

      const oldestRequest = index.openCursor()
      oldestRequest.onerror = () => reject(oldestRequest.error)
      oldestRequest.onsuccess = () => {
        const cursor = oldestRequest.result
        if (cursor) {
          oldest = (cursor.value as CachedFile).timestamp
        }

        const newestRequest = index.openCursor(null, 'prev')
        newestRequest.onerror = () => reject(newestRequest.error)
        newestRequest.onsuccess = () => {
          const cursor = newestRequest.result
          if (cursor) {
            newest = (cursor.value as CachedFile).timestamp
          }
          resolve({ oldest, newest })
        }
      }
    })
  }

  async function getMetadata(key: string): Promise<string | number | null> {
    return withDbOperation(
      (database) =>
        new Promise((resolve, reject) => {
          const transaction = database.transaction(METADATA_STORE, 'readonly')
          const store = transaction.objectStore(METADATA_STORE)
          const request = store.get(key)

          request.onerror = () => reject(request.error)
          request.onsuccess = () => {
            const result = request.result as CacheMetadata | undefined
            resolve(result?.value ?? null)
          }
        }),
    )
  }

  async function setMetadata(key: string, value: string | number): Promise<void> {
    await withDbOperation(
      (database) =>
        new Promise<void>((resolve, reject) => {
          const transaction = database.transaction(METADATA_STORE, 'readwrite')
          const store = transaction.objectStore(METADATA_STORE)
          const request = store.put({ key, value })

          request.onerror = () => reject(request.error)
          request.onsuccess = () => resolve()
        }),
    )
  }

  async function getBuildHash(): Promise<string | null> {
    return (await getMetadata('buildHash')) as string | null
  }

  async function setBuildHash(hash: string): Promise<void> {
    await setMetadata('buildHash', hash)
    await setMetadata('lastSync', Date.now())
  }

  async function has(path: string): Promise<boolean> {
    const file = await get(path)
    return file !== null
  }

  function close(): void {
    invalidateConnection()
  }

  return {
    isInitialized,
    isAvailable,
    cacheSize,
    init,
    get,
    set,
    remove,
    clear,
    has,
    getSize,
    evictIfNeeded,
    getStats,
    getMetadata,
    setMetadata,
    getBuildHash,
    setBuildHash,
    close,
  }
}

let cacheInstance: ReturnType<typeof useEvidenceCache> | null = null

export function getEvidenceCache(): ReturnType<typeof useEvidenceCache> {
  if (!cacheInstance) {
    cacheInstance = useEvidenceCache()
  }
  return cacheInstance
}

/**
 * Tear down the evidence cache on logout. Safe for customer sessions that never
 * opened admin reports — no-ops when the singleton was never created.
 */
export async function resetEvidenceCacheForLogout(): Promise<void> {
  if (!cacheInstance) {
    return
  }

  const instance = cacheInstance
  try {
    if (instance.isInitialized.value) {
      await instance.clear().catch((err) => {
        logger.warn('Failed to clear evidence cache on logout', err)
      })
    }
  } finally {
    instance.close()
    cacheInstance = null
  }
}
